import React, { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';
import { mountRootParcel } from 'single-spa';
import Parcel from 'single-spa-react/parcel';

import { createReactRenderer } from 'helpers/react-renderer';
import { InvoicingConfig } from 'root/apps/QuoteMachineIntegration/common';
import { useSale } from 'root/apps/QuoteMachineIntegration/hooks/useSale';
import InvoiceService from 'root/apps/QuoteMachineIntegration/services/InvoiceService';
import { InvoicePaymentModalProps } from 'root/apps/QuoteMachineIntegration/state/InvoicePaymentModalProps';
import { SystemJS } from 'root/apps/QuoteMachineIntegration/SystemJS';
import { getSessionData } from 'root/apps/QuoteMachineIntegration/utils/SessionData';
import { datadogRum } from 'root/datadog/datadogRum';

export const errorMessages = {
  FETCH_ERROR: 'There was an error fetching the invoices for this customer.',
};

function InvoicePaymentModal({
  transactionId,
  defaultValue,
  onCancel,
  onSubmit,
  onError,
}: InvoicePaymentModalProps) {
  const { sale } = useSale(transactionId);
  const {
    data: invoices,
    isLoading: loadingInvoices,
    isError: isErrorLoadingInvoices,
  } = useQuery({
    queryKey: ['fetchInvoices', sale],
    queryFn: () =>
      InvoiceService.getPayableInvoices(
        sale?.Customer.customerID ?? '',
        sale?.Customer.creditAccountID ?? ''
      ),
    enabled: !!sale,
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    window.showNotifier('working');
    window.hideNotifier('warning');
  }, []);

  if (isErrorLoadingInvoices) {
    window.hideNotifier('working');
    onError(errorMessages.FETCH_ERROR);
    return <></>;
  }

  if (sale && invoices && !loadingInvoices) {
    window.hideNotifier('working');
    return (
      <Parcel
        key="invoice-payment-modal"
        mountParcel={mountRootParcel}
        baseLocation="/register.php"
        skipCSSUnmount={true}
        sessionData={getSessionData(sale)}
        invoices={invoices.data}
        defaultValue={defaultValue}
        onCancel={onCancel}
        onSubmit={onSubmit}
        config={async () =>
          (await SystemJS.import<InvoicingConfig>('@lightspeed/invoicing')).default
            .InvoicePaymentModalConfig
        }
        handleError={(err: Error) => datadogRum.addError(err)}
        getProposalToken={InvoiceService.getProposalToken}
      />
    );
  }

  return <></>;
}

export const renderInvoicePaymentModal = (props: InvoicePaymentModalProps) => {
  const RenderedComponent = createReactRenderer({
    component: InvoicePaymentModal,
    selector: '[data-react-root="ReactModal"]',
  });

  RenderedComponent.mount({
    props: { ...props },
  });
};
